import type { TonoDeEtiqueta } from '@/components/ui/etiqueta';
import { formatoFecha } from '@/lib/utils/formato-precio';
import { pisoEnTexto, techoEnTexto } from '../components/barra-de-rangos';
import type {
  Action,
  CreateRulePayload,
  Cruce,
  PricingRule,
  RevisionDePrecio,
  RuleScope,
} from '../types/pricing-rules.types';
import { LIMITE_VACIO, accionesDe, nombreAutomatico, tieneNombrePropio } from './nueva-regla';
import {
  alcanceEnFrase,
  describirReglaCompleta,
  esDeUnProducto,
  esTodoElCatalogo,
  estadoDeRegla,
} from './reglas-en-palabras';
import { LA_DE } from './jerarquia';

/**
 * «Límites que aplican a este producto» de la ficha (lote B2, spec 246 punto 4;
 * maqueta #b2-limites), sin pantalla: se prueba solo. Lee `revisar-precio` con
 * el precio de hoy (lo que rige, lo pisado y el cruce, ya con la jerarquía) y la
 * lista de reglas (de dónde viene cada una y el redondeo).
 */

/** Los límites que se pueden agregar sólo para un producto (el movimiento y el redondeo, no). */
export type TipoParaUnProducto =
  | 'TECHO_FIJO'
  | 'PISO_FIJO'
  | 'MARGEN_MINIMO'
  | 'SOBRE_EL_MAS_BARATO'
  | 'BAJO_EL_MAS_BARATO';

export const TIPOS_PARA_UN_PRODUCTO: Array<{
  tipo: TipoParaUnProducto;
  titulo: string;
  unidad: 'Bs' | '%';
  lado: 'PISO' | 'TECHO';
}> = [
  { tipo: 'TECHO_FIJO', titulo: 'No vender por encima de Bs…', unidad: 'Bs', lado: 'TECHO' },
  { tipo: 'PISO_FIJO', titulo: 'No vender por debajo de Bs…', unidad: 'Bs', lado: 'PISO' },
  { tipo: 'MARGEN_MINIMO', titulo: 'No ganar menos de… %', unidad: '%', lado: 'PISO' },
  {
    tipo: 'SOBRE_EL_MAS_BARATO',
    titulo: 'No quedar más de… % arriba del más barato',
    unidad: '%',
    lado: 'TECHO',
  },
  {
    tipo: 'BAJO_EL_MAS_BARATO',
    titulo: 'No quedar más de… % debajo del más barato',
    unidad: '%',
    lado: 'PISO',
  },
];

/** Lo que se está por agregar, en la ficha. `encender`: con permiso de encender reglas. */
export interface NuevoLimite {
  tipo: TipoParaUnProducto;
  valor: string;
  encender: boolean;
}

/** La acción del límite que se escribió, o null si el número no sirve. */
export function accionDelLimite(n: Pick<NuevoLimite, 'tipo' | 'valor'>): Action | null {
  return accionesDe({ ...LIMITE_VACIO, tipo: n.tipo, valor: n.valor })?.[0] ?? null;
}

/** El alta de la regla de un solo producto, con su nombre automático. */
export function cuerpoDelLimite(sku: string, accion: Action, encender: boolean): CreateRulePayload {
  const scope: RuleScope = { skus: [sku] };
  return {
    name: nombreAutomatico([accion], scope),
    scope,
    conditions: { all: [] },
    actions: [accion],
    isActive: encender,
  } as CreateRulePayload;
}

/**
 * Si la regla es sólo de este producto: alcance exactamente este SKU, sin
 * categorías, marcas ni excepciones (auditoría H3, H8, H9). Una regla de varios
 * SKU también es del escalón «producto», pero no es «sólo este producto».
 */
export function esSoloDeEsteProducto(s: RuleScope | null | undefined, sku: string): boolean {
  return esDeUnProducto(s) && s?.skus?.[0] === sku;
}

/**
 * Las acciones de una regla con la de ese tipo cambiada por la nueva; el resto
 * (un redondeo, otro límite) queda como estaba (auditoría H2, H8). Si no tenía
 * una de ese tipo, se suma.
 */
export function conLaAccion(actions: Action[], nueva: Action): Action[] {
  return actions.some((a) => a.type === nueva.type)
    ? actions.map((a) => (a.type === nueva.type ? nueva : a))
    : [...actions, nueva];
}

/**
 * La regla que corre, sólo de este producto, que ya pone un límite de ese tipo:
 * la que se edita en vez de crear otra igual en el mismo escalón (auditoría H8).
 * Ronda 2 B2: sin una que corra, vale la APAGADA de exactamente este SKU y tipo
 * (una banda de la ficha convertida): se edita esa en vez de crear una segunda.
 */
export function limitePropio(
  reglas: PricingRule[],
  sku: string,
  tipo: Action['type'],
  ahora: Date = new Date()
): PricingRule | null {
  const deEsteTipo = (r: PricingRule) =>
    esSoloDeEsteProducto(r.scope, sku) && r.actions.some((a) => a.type === tipo);
  const apagada = (r: PricingRule) => {
    const estado = estadoDeRegla(r, ahora);
    return !estado.corre && estado.porque === 'APAGADA';
  };
  return (
    reglas.find((r) => estadoDeRegla(r, ahora).corre && deEsteTipo(r)) ??
    reglas.find((r) => apagada(r) && deEsteTipo(r)) ??
    null
  );
}

/** Lo que se hace con el límite nuevo al guardar: editar la regla propia del mismo tipo, o crear una. */
export type CambioDelLimite =
  | { tipo: 'EDITAR'; regla: PricingRule; actions: Action[]; name: string }
  | { tipo: 'CREAR'; cuerpo: CreateRulePayload };

/**
 * Si ya hay un límite de ese tipo sólo de este producto, se edita esa acción (y
 * se renombra si su nombre era el automático); si no, se crea uno (auditoría H8:
 * crear otro dejaba dos del mismo escalón y decidía el más exigente).
 */
export function cambioDelLimite(
  reglas: PricingRule[],
  sku: string,
  accion: Action,
  encender: boolean,
  ahora: Date = new Date()
): CambioDelLimite {
  const propia = limitePropio(reglas, sku, accion.type, ahora);
  if (!propia) return { tipo: 'CREAR', cuerpo: cuerpoDelLimite(sku, accion, encender) };
  const actions = conLaAccion(propia.actions, accion);
  return {
    tipo: 'EDITAR',
    regla: propia,
    actions,
    name: tieneNombrePropio(propia) ? propia.name : nombreAutomatico(actions, propia.scope ?? {}),
  };
}

const TIPO_DE_LA_ACCION: Partial<Record<Action['type'], TipoParaUnProducto>> = {
  FORCE_FLOOR: 'PISO_FIJO',
  FORCE_MAX_PRICE: 'TECHO_FIJO',
  FORCE_MIN_MARGIN: 'MARGEN_MINIMO',
  MAX_ABOVE_COMPETITOR_MIN: 'SOBRE_EL_MAS_BARATO',
  MAX_BELOW_COMPETITOR_MIN: 'BAJO_EL_MAS_BARATO',
};

/**
 * «Cambiar el límite de este producto» en el conflicto (auditoría H8): abre con
 * el tipo y el valor del límite que choca — el de este producto si es uno de
 * los dos; si no, el que pide el piso. null si no se sabe cuál.
 */
export function limiteQueChoca(
  rev: RevisionDePrecio,
  reglas: PricingRule[],
  sku: string
): Pick<NuevoLimite, 'tipo' | 'valor'> | null {
  const c = rev.cruce;
  if (!c.vacio || c.min == null || c.max == null) return null;
  const porId = new Map(reglas.map((r) => [r.id, r]));
  const chocan = rev.rangos.filter(
    (r) =>
      r.aviso == null &&
      ((r.min != null && Math.abs(r.min - c.min!) < 0.005) ||
        (r.max != null && Math.abs(r.max - c.max!) < 0.005))
  );
  const elegido =
    chocan.find((r) => esSoloDeEsteProducto(porId.get(r.ruleId)?.scope, sku)) ?? chocan[0];
  const regla = elegido ? porId.get(elegido.ruleId) : undefined;
  const accion = regla?.actions.find((a) => TIPO_DE_LA_ACCION[a.type] === elegido!.tipo);
  if (!accion) return null;
  const v = 'value' in accion ? accion.value : 'percent' in accion ? accion.percent : null;
  return v == null
    ? null
    : { tipo: elegido!.tipo as TipoParaUnProducto, valor: String(v).replace('.', ',') };
}

/** El tramo permitido en palabras: «de 5.000,00 a 9.144,00», «desde 4.775,92», «hasta 9.144,00». null si no hay o choca. */
export function permitidoEnTexto(c: Cruce): string | null {
  if (c.vacio) return null;
  // Auditoría H17: «Queda permitido de 5.000,00 a 9.144,00», como la maqueta.
  if (c.min != null && c.max != null) return `de ${pisoEnTexto(c.min)} a ${techoEnTexto(c.max)}`;
  if (c.min != null) return `desde ${pisoEnTexto(c.min)}`;
  if (c.max != null) return `hasta ${techoEnTexto(c.max)}`;
  return null;
}

export interface FilaDeLimite {
  clave: string;
  limite: string;
  /** «desde 4.775,92», «hasta 9.144,00». null sin número (no se pudo medir). */
  tramo: string | null;
  origen: string;
  origenDetalle: string | null;
  estado: { texto: string; tono: TonoDeEtiqueta } | null;
  /** Lote B2: la que no rige, con quién decide. */
  noRige: string | null;
}

/** El producto, para saber qué redondeo lo alcanza (lo mismo que `alcanzaAlProducto` del motor). */
export interface ProductoDelPanel {
  sku: string;
  category: string | null;
  brand: string | null;
}

const normal = (s: string | null | undefined) => (s ?? '').trim().toLocaleLowerCase('es');
const estaEn = (lista: string[], v: string | null) =>
  normal(v) !== '' && lista.some((x) => normal(x) === normal(v));

export function alcanzaAlProducto(s: RuleScope, p: ProductoDelPanel): boolean {
  if (s.excludedSkus?.includes(p.sku)) return false;
  if (s.categories?.length && !estaEn(s.categories, p.category)) return false;
  if (s.brands?.length && !estaEn(s.brands, p.brand)) return false;
  if (s.skus?.length && !s.skus.includes(p.sku)) return false;
  return true;
}

const especificidad = (s: RuleScope) =>
  s.skus?.length ? 2 : s.brands?.length || s.categories?.length ? 1 : 0;

/**
 * El redondeo que le toca (el más puntual; con el mismo alcance, menor
 * prioridad): como `elegirRedondeo`. Sólo entre las que corren hoy, como el
 * motor (`loadRulesAndDiscounts`): encendida, sin condiciones y dentro de su
 * vigencia (auditoría H18). El grupo ya no cuenta: `groupId` es null en todas
 * desde el 4B-datos.
 */
export function redondeoDelProducto(
  reglas: PricingRule[],
  p: ProductoDelPanel,
  ahora: Date = new Date()
): PricingRule | null {
  const candidatas = reglas.filter(
    (r) =>
      estadoDeRegla(r, ahora).corre &&
      r.actions.some((a) => a.type === 'ROUND_PRICE') &&
      alcanzaAlProducto(r.scope ?? {}, p)
  );
  return (
    [...candidatas].sort(
      (a, b) =>
        especificidad(b.scope ?? {}) - especificidad(a.scope ?? {}) ||
        (a.priority ?? 100) - (b.priority ?? 100)
    )[0] ?? null
  );
}

/**
 * «Sólo este producto» sólo si la regla es exactamente de este SKU (auditoría
 * H9): una de varios productos, o de un producto y una categoría, dice su
 * nombre y a quién alcanza.
 */
function deDondeViene(
  ruleName: string,
  alcance: string | undefined,
  regla: PricingRule | undefined,
  sku: string
): { origen: string; origenDetalle: string | null } {
  if (regla && esSoloDeEsteProducto(regla.scope, sku))
    return {
      origen: 'Sólo este producto',
      // Lote C2b (P2 de B2; maqueta): «lo agregó Roberto Méndez el 2 oct», si se sabe quién;
      // en modo soporte, «lo agregó Ana Pérez, del soporte de Prixia, el 2 oct» (auditoría H3).
      origenDetalle: regla?.createdAt
        ? regla.creadaPor
          ? `lo agregó ${regla.creadaPor}${regla.creadaPorSoporte ? ', del soporte de Prixia,' : ''} el ${formatoFecha(regla.createdAt)}`
          : `creada el ${formatoFecha(regla.createdAt)}`
        : null,
    };
  const scope = regla?.scope ?? {};
  return {
    origen: `«${ruleName}»`,
    origenDetalle: regla
      ? esTodoElCatalogo(scope)
        ? 'todo el catálogo'
        : alcanceEnFrase(scope)
      : alcance === 'CATALOGO'
        ? 'todo el catálogo'
        : null,
  };
}

function tramoDe(min: number | null, max: number | null): string | null {
  if (min != null && max != null) return `de ${pisoEnTexto(min)} a ${techoEnTexto(max)}`;
  if (min != null) return `desde ${pisoEnTexto(min)}`;
  if (max != null) return `hasta ${techoEnTexto(max)}`;
  return null;
}

/** Las filas del panel: las que rigen, las que no rigen y el redondeo, que va al final. */
export function filasDelPanel(
  rev: RevisionDePrecio,
  reglas: PricingRule[],
  producto: ProductoDelPanel
): FilaDeLimite[] {
  const porId = new Map(reglas.map((r) => [r.id, r]));
  // Ronda final B2: una regla con dos límites (piso y margen) da dos filas; cada
  // una se marca y se describe con lo suyo, por regla y tipo.
  const claveDe = (r: { ruleId: string; tipo: string }) => `${r.ruleId}|${r.tipo}`;
  const rompe = new Set(rev.rompe.map(claveDe));
  const textoDe = (regla: PricingRule, tipo: string): string => {
    const propias = regla.actions.filter((a) => TIPO_DE_LA_ACCION[a.type] === tipo);
    return describirReglaCompleta(propias.length > 0 ? propias : regla.actions);
  };
  const filas: FilaDeLimite[] = rev.rangos.map((r, i) => {
    const regla = porId.get(r.ruleId);
    return {
      clave: `${r.ruleId}-${i}`,
      limite: regla ? textoDe(regla, r.tipo) : r.ruleName,
      tramo: r.aviso != null ? null : tramoDe(r.min, r.max),
      ...deDondeViene(r.ruleName, r.alcance, regla, producto.sku),
      estado:
        r.aviso != null
          ? { texto: 'no se puede medir', tono: 'neutro' }
          : rompe.has(claveDe(r))
            ? { texto: 'no se cumple', tono: 'bloqueo' }
            : { texto: 'se cumple', tono: 'ok' },
      noRige: null,
    };
  });
  (rev.pisados ?? []).forEach((p, i) => {
    const regla = porId.get(p.ruleId);
    filas.push({
      clave: `pisada-${p.ruleId}-${i}`,
      limite: regla ? textoDe(regla, p.tipo) : p.ruleName,
      tramo: tramoDe(p.min, p.max),
      ...deDondeViene(p.ruleName, p.alcance, regla, producto.sku),
      estado: { texto: 'no rige', tono: 'neutro' },
      noRige: `Decide «${p.pisadoPor.ruleName}», ${LA_DE[p.pisadoPor.alcance]}: es más específica.`,
    });
  });
  const redondeo = redondeoDelProducto(reglas, producto);
  if (redondeo) {
    const scope = redondeo.scope ?? {};
    const soloEste = esSoloDeEsteProducto(scope, producto.sku);
    filas.push({
      clave: `redondeo-${redondeo.id}`,
      limite: describirReglaCompleta(redondeo.actions.filter((a) => a.type === 'ROUND_PRICE')),
      tramo: null,
      origen: soloEste ? 'Sólo este producto' : `«${redondeo.name}»`,
      origenDetalle: soloEste
        ? null
        : esTodoElCatalogo(scope)
          ? 'todo el catálogo'
          : alcanceEnFrase(scope),
      estado: null,
      noRige: null,
    });
  }
  return filas;
}

/** Las de margen que no se pueden medir por falta de costo (estado «Sin costo»). */
export function margenSinMedir(rev: RevisionDePrecio): string[] {
  return [
    ...new Set(
      rev.rangos.filter((r) => r.tipo === 'MARGEN_MINIMO' && r.aviso != null).map((r) => r.ruleName)
    ),
  ];
}

/**
 * «Conflicto entre reglas»: quién pide el piso y quién deja el techo, con sus
 * números. «El límite de este producto» sólo si es exactamente de este SKU
 * (auditoría H9).
 */
export function conflictoEnTexto(
  rev: RevisionDePrecio,
  reglas: PricingRule[],
  sku: string
): string | null {
  const c = rev.cruce;
  if (!c.vacio || c.min == null || c.max == null) return null;
  const piso = rev.rangos.find(
    (r) => r.aviso == null && r.min != null && Math.abs(r.min - c.min!) < 0.005
  );
  const techo = rev.rangos.find(
    (r) => r.aviso == null && r.max != null && Math.abs(r.max - c.max!) < 0.005
  );
  const porId = new Map(reglas.map((r) => [r.id, r]));
  const quien = (r: typeof piso) =>
    !r
      ? 'una regla'
      : esSoloDeEsteProducto(porId.get(r.ruleId)?.scope, sku)
        ? 'el límite de este producto'
        : `«${r.ruleName}»`;
  const sujeto = quien(piso);
  return `${sujeto.charAt(0).toUpperCase()}${sujeto.slice(1)} pide desde ${pisoEnTexto(c.min)} y ${quien(techo)} deja hasta ${techoEnTexto(c.max)}. El precio no se mueve hasta que lo resuelvas.`;
}

/**
 * Qué cambia con el límite que se va a agregar (maqueta: «Con esto, el techo de
 * este producto baja de 9.144,00 a 6.500,00.»), comparando el cruce de antes y
 * el del borrador. Termina con cómo se crea, o con qué regla se cambia si ya
 * había un límite de ese tipo sólo de este producto (`editada`, auditoría H8).
 */
export function quePasaConElNuevo(
  antes: RevisionDePrecio,
  despues: RevisionDePrecio,
  tipo: TipoParaUnProducto,
  editada: { ruleId: string; name: string } | null = null
): string {
  const COMO = editada
    ? ` Al tocar «Guardar cambios» se cambia «${editada.name}», que ya era de este producto (sólo ese límite de la regla). El resto del catálogo no cambia.`
    : ' Se crea al tocar «Guardar cambios», como una regla en Reglas con alcance «sólo este producto». El resto del catálogo no cambia.';
  const nuevo = despues.rangos.find((r) => r.ruleId === (editada?.ruleId ?? '__borrador__'));
  if (nuevo?.aviso != null)
    return `Con esto no cambia nada por ahora: ${nuevo.aviso.toLowerCase()}.${COMO}`;
  if (despues.cruce.vacio && despues.cruce.min != null && despues.cruce.max != null)
    return `Con esto quedaría un conflicto entre reglas: el piso (${pisoEnTexto(despues.cruce.min)}) queda arriba del techo (${techoEnTexto(despues.cruce.max)}).${COMO}`;
  const lado = TIPOS_PARA_UN_PRODUCTO.find((t) => t.tipo === tipo)!.lado;
  const a = lado === 'PISO' ? antes.cruce.min : antes.cruce.max;
  const d = lado === 'PISO' ? despues.cruce.min : despues.cruce.max;
  const txt = lado === 'PISO' ? pisoEnTexto : techoEnTexto;
  const cual = lado === 'PISO' ? 'el piso' : 'el techo';
  if (d == null) return `Con esto, ${cual} de este producto no cambia.${COMO}`;
  if (a == null) return `Con esto, ${cual} de este producto queda en ${txt(d)}.${COMO}`;
  if (Math.abs(a - d) < 0.005)
    return `Con esto, ${cual} de este producto no cambia: sigue en ${txt(d)}.${COMO}`;
  return `Con esto, ${cual} de este producto ${d < a ? 'baja' : 'sube'} de ${txt(a)} a ${txt(d)}.${COMO}`;
}
